import { Injectable } from '@angular/core';
import { Http, Response } from '@angular/http';
import { ParentService } from 'app/pages/A2-utils/ParentClass/ParentService';
import { BehaviorSubject, Observable } from 'rxjs';
import { IDeposit, IDepositDetail, IDepositResponse, ILockBox, IPartialDepositDetail, IPosted } from '../interfaces/deposit.interface';
import { map } from 'rxjs/operators';
import { constant } from 'app/app.constants';

@Injectable()
export class ConciliationService extends ParentService {
  behaviorSubjDeposit = new BehaviorSubject<{ action: string, data: any }>(null);
  behaviorSubjDepositObs$ = this.behaviorSubjDeposit.asObservable();

  private postedEdit = new BehaviorSubject<{ action: string, postedEdit: any }>({ action: '', postedEdit: null });
  postedEdit$ = this.postedEdit.asObservable();

  private resetElectronic = new BehaviorSubject<boolean>(false);
  resetElectronic$ = this.resetElectronic.asObservable();

  private claimsCount = new BehaviorSubject<number>(0);
  claimsCount$ = this.claimsCount.asObservable();

  constructor(private http: Http) {
    super();
  }


  getDetail(depositId: number): Observable<any> {
    const url = `${constant.URL_API_DEPOSIT_GET_DETAIL}/${depositId}`;
    return this.http
      .get(url, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getDeposit(input: IDeposit): Observable<IDepositResponse[]> {
    return this.http
      .post(constant.URL_API_DEPOSIT_SEARCH, input, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getAccountSummaryReport(depositIds: number[]): Observable<Blob> {
    return this.http
      .post(constant.URL_API_DEPOSIT_ACCOUNT_SUMMARY_REPORT, { depositids: depositIds }, this.getFileHeaders())
      .pipe(map((response: Response) => response.blob()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getPostedWithoutDeposit(input: IDeposit) {
    const stringUrl = `${constant.URL_API_DEPOSIT_POSTED_WITHOUT_DEPOSIT}` +
    `?depositdate=${input.depositdate}&includeZeroBalance=${input.includeZeroBalance}`;
    return this.http
      .get(stringUrl, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getAllegedDepositDetails(depositId: number): Observable<any> {
    return this.http
      .get(`${constant.URL_API_DEPOSIT_ALLEGED}?depositId=${depositId}`, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  saveDeposit(body: IDeposit): Observable<any> {
    return this.http
      .post(constant.URL_API_DEPOSIT_BASE, body, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  updateDeposit(body: IDeposit): Observable<any> {
    return this.http
      .put(constant.URL_API_DEPOSIT_BASE, body, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  saveDepositDetail(body: IDepositDetail): Observable<any> {
    return this.http
      .post(constant.URL_API_DEPOSIT_DETAIL_SAVE, body, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  putDepositDetail(body: IDepositDetail): Observable<any> {
    return this.http
      .put(constant.URL_API_DEPOSIT_DETAIL_SAVE, body, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getDepositStatus(): Observable<any> {
    return this.http
      .get(`${constant.URL_API_DEPOSIT_STATUS}?depositStatus=true`, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getPostedByCheckNumber(checkNumber: string, depositId: number): Observable<any> {
    const url = `${constant.URL_API_DEPOSIT_GROUPED_BY_CHECKNUMBER}?checkNumber=${checkNumber}&depositId=${depositId}`;
    return this.http
      .get(url, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getPayorsByDescription(request: { filter: string }): Observable<any> {
    const url = `${constant.URL_API_DEPOSIT_POSTED_DISTINCT_PAYORS}?payor=${request.filter}`;
    return this.http
      .get(url, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getAccountNumber(request: { filter: string }): Observable<any> {
    const url = `${constant.URL_API_DEPOSIT_DISTINCT_ACCOUNT}?bankAccount=${request.filter}`;
    return this.http
      .get(url, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getAccountNumberList(showInactive: boolean): Observable<any> {
    const url = `${constant.URL_API_DEPOSIT_BANK_ACCOUNT}?showInactive=${showInactive}`;
    return this.http
      .get(url, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getDepositHistory(depositId: number): Observable<any> {
    const url = `${constant.URL_API_DEPOSIT_HISTORY}?depositId=${depositId}`;
    return this.http
      .get(url, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getPoBox(accountNumber: string): Observable<any> {
    const url = `${constant.URL_API_DEPOSIT_GET_PO_BOX}?accountNumber=${accountNumber}`;
    return this.http
      .get(url, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getLockBox(input: IDeposit): Observable<any> {
    return this.http
      .post(constant.URL_API_DEPOSIT_LOCKBOX_SEARCH, input, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  updateLockBox(body: ILockBox): Observable<any> {
    return this.http
      .put(constant.URL_API_DEPOSIT_LOCKBOX_UPDATE, body, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  updateOfficeDeposit(body: ILockBox): Observable<any> {
    return this.http
      .put(constant.URL_API_DEPOSIT_OFFICE_DEPOSIT_BASE, body, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  saveOfficeDeposit(body: ILockBox): Observable<any> {
    return this.http
      .post(constant.URL_API_DEPOSIT_OFFICE_DEPOSIT_BASE, body, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  saveMasiveOfficeDeposit(body: any): Observable<any> {
    return this.http
      .post(constant.URL_API_DEPOSIT_OFFICE_DEPOSIT_MASSIVE, body, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  updateMasiveOfficeDeposit(list: any[]): Observable<any> {
    return this.http
      .put(constant.URL_API_DEPOSIT_OFFICE_DEPOSIT_MASSIVE, list, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getOfficeDeposit(input: IDeposit): Observable<any> {
    return this.http
      .post(constant.URL_API_DEPOSIT_OFFICE_DEPOSIT_SEARCH, input, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getOfficeDepositGrouped(input: any): Observable<any> {
    return this.http
      .post(constant.URL_API_DEPOSIT_OFFICE_DEPOSIT_OFFICE_DEP_GROUPED, input, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  deleteOfficeDeposit(officeDepositId: number): Observable<any> {
    return this.http
      .delete(`${constant.URL_API_DEPOSIT_OFFICE_DEPOSIT_BASE}/${officeDepositId}`, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getPosted(input: IPosted): Observable<any> {
    return this.http
      .post(constant.URL_API_DEPOSIT_POSTED_SEARCH, input, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  deletePosted(postedId: number): Observable<any> {
    return this.http
      .delete(`${constant.URL_API_DEPOSIT_POSTED_BASE}/${postedId}`, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  updatePosted(body: IPosted): Observable<any> {
    return this.http
      .put(constant.URL_API_DEPOSIT_POSTED_BASE, body, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  postedCopy(input: IPosted): Observable<any> {
    return this.http
      .post(constant.URL_API_DEPOSIT_POSTED_COPY, input, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  deleteDeposit(depositId: number): Observable<any> {
    return this.http
      .delete(`${constant.URL_API_DEPOSIT_BASE}/${depositId}`, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  createMassiveDepositDetails(input: IPartialDepositDetail[]): Observable<IDepositDetail[]> {
    return this.http
      .post(constant.URL_API_DEPOSIT_DETAIL_CREATE_MASSIVE, input, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  refreshDetailsPayor(depositId: number): Observable<string> {
    return this.http
      .patch(`${constant.URL_API_DEPOSIT_REFRESH_DETAIL_PAYORS}/${depositId}`, null, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  depositDetailDeleteAll(depositIdlist: number[]): Observable<any> {
    return this.http
      .put(constant.URL_API_DEPOSIT_DETAIL_DELETE_ALL, depositIdlist, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  updateLockBoxStatus(datePicked: Date): Observable<any> {
    const url = `${constant.URL_API_DEPOSIT_POSTED_UPDATE_LOCKBOX_STATUS}?date=${datePicked}`;
    return this.http
      .get(url, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  updateOfficeDepositStatus(pickedDate: Date): Observable<any> {
    const url = `${constant.URL_API_DEPOSIT_OFFICE_DEPOSIT_UPDATE_STATUS}?date=${pickedDate}`;
    return this.http
      .get(url, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  getOfficeDepositDescriptionList(tranDate: string): Observable<any> {
    const url = `${constant.URL_API_DEPOSIT_GET_OFFICE_DEPOSIT_DESCRIPTION}?tranDate=${tranDate}`;
    return this.http
    .get(url, this.getHeader())
    .pipe(map((response: any) => response.json()))
    .catch((error: any) => Observable.throw(error.json()));
  }

  getElectronicList(showInactive: boolean, checkDate: Date): Observable<any> {
    const url = `${constant.URL_ELECTRONIC_SEARCH}?showInactive=${showInactive}&checkDate=${checkDate}`;
    return this.http
    .get(url, this.getHeader())
    .pipe(map((response: any) => response.json()))
    .catch((error: any) => Observable.throw(error.json()));
  }

  getElectronicDetailList(electronicId: number): Observable<any> {
    const url = `${constant.URL_ELECTRONIC_GET_DETAIL_BY_ID}?electronicId=${electronicId}`;
    return this.http
    .get(url, this.getHeader())
    .pipe(map((response: any) => response.json()))
    .catch((error: any) => Observable.throw(error.json()));
  }

  getUpdateElectronicStatus(): Observable<any> {
    return this.http
    .get(`${constant.URL_ELECTRONICT}/UpdateElectronicStatus`, this.getHeader())
    .pipe(map((response: any) => response.json()))
    .catch((error: any) => Observable.throw(error.json()));
  }

  getRemitSummaryById(electronicId: number): Observable<any> {
    const url = `${constant.URL_ELECTRONIC_GET_REMIT_SUMMARY_BY_ID}?electronicId=${electronicId}`;
    return this.http
    .get(url, this.getHeader())
    .pipe(map((response: any) => response.json()))
    .catch((error: any) => Observable.throw(error.json()));
  }

  getClaimDetailById(input: any): Observable<any> {
    return this.http
      .post(constant.URL_ELECTRONIC_GET_CLAIM_DETAIL_BY_ID, input, this.getHeader())
      .pipe(map((response: any) => response.json()))
      .catch((error: any) => Observable.throw(error.json()));
  }

  deleteElectronicById(electronicId: number): Observable<any> {
    const url = `${constant.URL_ELECTRONIC_DELETE_BY_ID}?electronicId=${electronicId}`;
    return this.http
    .delete(url, this.getHeader())
    .pipe(map((response: any) => response.json()))
    .catch((error: any) => Observable.throw(error.json()));
  }


  setPostedEdit(action, postedEdit: any) {
    this.postedEdit.next({ action, postedEdit });
  }

  resetElectronicId(resetElectronic: boolean) {
    this.resetElectronic.next(resetElectronic);
  }

  setClaimsCount(claimsCount: number) {
    this.claimsCount.next(claimsCount);
  }
}



// WEBPACK FOOTER //
// ./src/app/core/services/conciliation.service.ts